import React, { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Container, Stack, Group, Title, Text, SimpleGrid, Badge, Drawer, Tabs, Card } from '@mantine/core';
import AreaAssessmentCard from './SectionAssessmentCard';
import ControlEvaluationWizard from '../riskFlow/ControlEvaluationWizard';
import ChartsSummary from './ChartsSummary';
import ActionPlan from '../ActionPlan';
import RiskMonitors from '../RiskMonitors';
import AssessmentCanvasV2 from './AssessmentCanvasV2';
import ModeSwitcher from '../components/ModeSwitcher';

function useInitialRef(value) {
  const ref = useRef(value);
  // lock the initial value; do not update on changes
  return ref;
}

export default function AssessmentCanvas({ modelId, assessmentUuid }) {
  // Feature flag for V2 components - change to true to use improved version
  const USE_V2_CANVAS = true; // Set to true to enable new UI improvements
  
  // All hooks must be called before any early returns
  const risksState = useSelector((state) => state.risks.models[modelId] || {});
  const riskFormData = useSelector((state) => state.risks.models[modelId]?.riskFormData || {});
  const residualRiskData = useSelector((state) => state.residualRisk.byModel[modelId] || {});

  const originalRiskFormDataRef = useInitialRef(riskFormData);

  // Get model name from either risks or security state
  const securityState = useSelector((state) => state.security.models[modelId] || {});
  const modelName = risksState?.modelName || securityState?.modelName || 'Unknown Model';

  const sections = useMemo(() => Object.keys(riskFormData || {}), [riskFormData]);

  // Drawer for evaluating actions on a specific risk
  const [evalDrawer, setEvalDrawer] = useState({ opened: false, riskArea: null, source: null });

  const handleOpenEvaluation = ({ riskArea, sourceName }) => {
    const sourceObj = (riskFormData?.[riskArea]?.riskSources || []).find((s) => s?.name === sourceName) || { name: sourceName };
    setEvalDrawer({ opened: true, riskArea, source: sourceObj });
  };

  const handleCloseEvaluation = () => setEvalDrawer({ opened: false, riskArea: null, source: null });

  // small header stats
  const totalRisks = useMemo(() => {
    return Object.values(riskFormData).reduce((acc, sec) => {
      const counts = Object.values(sec.sectionRisks || {}).reduce((c, arr) => c + (Array.isArray(arr) ? arr.length : 0), 0);
      return acc + counts;
    }, 0);
  }, [riskFormData]);

  // Early return for V2 after all hooks are called
  if (USE_V2_CANVAS) {
    return <AssessmentCanvasV2 modelId={modelId} assessmentUuid={assessmentUuid} />;
  }

  return (
    <Container fluid px="xl" py="xl">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={3}>{modelName} — Assessment Canvas</Title>
            <Text size="sm" c="dimmed">Safety risks and scenarios overview</Text>
          </div>
          <ModeSwitcher
            currentMode="copilot"
            onModeChange={(mode) => {
              if (mode === 'autopilot') {
                window.location.href = `/autopilot-assessment/${modelId}/${assessmentUuid}`;
              }
            }}
            showAsSwitch={true}
          />
        </Group>

        <Group gap="md" align="center" mt="sm">
          <Badge variant="light" color="indigo" size="lg">{totalRisks} risks</Badge>
        </Group>

        <Tabs defaultValue="overview" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            <Tabs.Tab value="cards">Assessment Cards</Tabs.Tab>
            <Tabs.Tab value="actionPlan">Action Plan</Tabs.Tab>
            <Tabs.Tab value="analyzers">Monitors & Analyzers</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <ChartsSummary riskFormData={riskFormData} />
          </Tabs.Panel>

          <Tabs.Panel value="cards" pt="md">
            <SimpleGrid cols={{ base: 1, sm: 2, md: 2, lg: 3 }} spacing="lg">
              {sections.map((sectionName) => (
                <AreaAssessmentCard
                  key={sectionName}
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  sectionName={sectionName}
                  sectionData={riskFormData[sectionName]}
                  originalSectionData={originalRiskFormDataRef.current?.[sectionName]}
                  residualRiskData={residualRiskData}
                  onEvaluateAction={handleOpenEvaluation}
                />
              ))}
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="actionPlan" pt="md">
            <Card withBorder radius="md" p="md">
              <ActionPlan modelId={modelId} assessmentUuid={assessmentUuid} riskFormData={riskFormData} />
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="analyzers" pt="md">
            <Card withBorder radius="md" p="md">
              <RiskMonitors modelId={modelId} />
            </Card>
          </Tabs.Panel>
        </Tabs>

        <Drawer
          opened={evalDrawer.opened}
          onClose={handleCloseEvaluation}
          position="right"
          size="xl"
          title="Evaluate Actions"
        >
          {evalDrawer.opened && (
            <ControlEvaluationWizard
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              riskArea={evalDrawer.riskArea}
              source={evalDrawer.source}
            />
          )}
        </Drawer>
      </Stack>
    </Container>
  );
}
